import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mandaBemApi } from "@/services";
import { useTenant } from "./useTenant";
import { useAllIntegrationsStatus } from "./useIntegrationsStatus";

interface MandaBemStatus {
  connected: boolean;
  plataforma_id?: string;
}

interface ConnectParams {
  plataforma_id: string;
  plataforma_chave: string;
}

interface MandaBemShippingService {
  id: string;
  name: string;
  price: string;
  currency: string;
  delivery_time: number;
  delivery_range: {
    min: number;
    max: number;
  };
  company: {
    id: number;
    name: string;
    picture: string;
  };
  provider: string;
}

interface CalculateParams {
  cepOrigem: string;
  cepDestino: string;
  peso?: number;
  comprimento?: number;
  largura?: number;
  altura?: number;
}

export function useMandaBemStatus() {
  const { data: integrations, isLoading } = useAllIntegrationsStatus();

  const data = useMemo((): MandaBemStatus => {
    const integration = integrations?.find(
      (i) => i.provider === "manda_bem" && i.is_active
    );
    if (!integration) return { connected: false };
    const metadata = integration.metadata as Record<string, unknown> | null;
    return {
      connected: true,
      plataforma_id: (metadata?.plataforma_id as string) || undefined,
    };
  }, [integrations]);

  return { data, isLoading };
}

export function useConnectMandaBem() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: ConnectParams) => {
      return await mandaBemApi.connect(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useDisconnectMandaBem() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      await mandaBemApi.disconnect();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useMandaBemCalculateShipping() {
  return useMutation({
    mutationFn: async (params: CalculateParams): Promise<MandaBemShippingService[]> => {
      const data = await mandaBemApi.calculateShipping(params);
      return data as MandaBemShippingService[];
    },
  });
}

export function useMandaBemCreateShipment() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      return await mandaBemApi.createShipment(params);
    },
    onSuccess: (_, variables) => {
      const saleId = variables.sale_id as string;
      queryClient.invalidateQueries({ queryKey: ["shipments", saleId, tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenant?.id] });
    },
  });
}
